import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import draftToHtml from "draftjs-to-html";
import { Editor } from "react-draft-wysiwyg";
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import MuiFormControlLabel from "@mui/material/FormControlLabel";

import Close from "mdi-material-ui/Close";
import BottomResize from "mdi-material-ui/ResizeBottomRight";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";

import DefaultPalette from "@core/theme/palettes";
import FormButtons from "views/components/formbuttons";
import FormHeader from "views/components/formheader";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { EditorOption } from "@core/layouts/utils";
import moment from "moment";
import {
  isSpaceValidateTextField,
} from "@core/utils/Const";
import Loader from "@core/helper/Loader";

const customTheme = DefaultPalette();

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const Img = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    width: 200,
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const AddAnnouncement = (props) => {
  const { open, toggle, type, heading, onSubmitAnnouncement, updateData } =
    props;
  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [size, setSize] = useState({ y: 300 });
  const [width, setWidth] = useState(window.innerWidth);
  const [selectType, setSelectType] = useState(0);

  const handleWindowResize = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
      setValue("image", acceptedFiles);
      setRemoveImage(false);
    },
    onDropRejected: () => {
      setRemoveImage(true);
      setFiles([]);
      toast.error("You can only upload 1 file & maximum size of 2 MB.", {
        duration: 2000,
      });
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <FileDocumentOutline />;
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setValue("image", "");
    setFilename("");
    setRemoveImage(true);
  };

  const fileList = files.map((file) => (
    <ListItem
      key={file.name}
      sx={{ justifyContent: "space-between", display: "flex" }}
    >
      <div
        className="file-details"
        style={{
          display: "flex",
          alignItems: "center",
          width: "90%",
        }}
      >
        <div className="file-preview">{renderFilePreview(file)}</div>
        <div
          style={{
            marginLeft: 2,
            flexDirection: "column",
            width: "92%",
          }}
        >
          <Typography
            className="file-name"
            variant="body2"
            sx={{ wordWrap: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <BottomResize fontSize="small" />
      </IconButton>
    </ListItem>
  ));

  const defaultValues = {
    title: "",
    description: "",
    image: "",
    subTitle: "",
    type: 0,
    priority: false,
    sequence: 0,
    notification: false,
    isPin: false,
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    setValue("title", "");
    setValue("description", "");
    setValue("image", "");
    setValue("subTitle", "");
    setValue("type", "");
    setValue("priority", false);
    setValue("sequence", 0);
    setValue("notification", false);
    setValue("isPin", false);
    setFiles([]);
    setFilename("");
  }, [setValue]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        title: updateData.title,
        description: EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(`${updateData.content}`)
          )
        ),
        image: updateData.image,
        subTitle: updateData.sub_title,
        type: updateData.type,
        priority: updateData.priority,
        sequence: updateData.order_sequence,
        notification: updateData.isNotification,
        isPin: updateData.is_pin,
        pin_date: moment().format(),
      };
      setSelectType(event.type);
      setFilename(event.image || "");
      setValue("title", event.title || "");
      setValue("description", event.description || "");
      setValue("image", event.image || "");
      setValue("subTitle", event.subTitle || "");
      setValue("type", event.type || 0);
      setValue("priority", event.priority || false);
      setValue("sequence", event.sequence || 0);
      setValue("notification", event.notification || false);
      setValue("isPin", event.isPin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, type]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    clearErrors();
    if (type === "add") {
      reset(defaultValues);
      setFiles([]);
      setFilename("");
    }
    setRemoveImage(false);
    toggle();
  };

  const submitAnnouncement = (data, selectImage) => {
    const commonPayload = {
      title: data.title,
      subTitle: data.subTitle,
      content: data.description
        ? draftToHtml(convertToRaw(data.description.getCurrentContent()))
        : "",
      image: files.length > 0 ? selectImage : "",
      type: data.type,
      priority: data.priority,
      sequence: data.sequence,
      isNotification: data.notification,
      isPin: data.isPin,
      pin_date: moment().format(),
    };
    Loader.isLoading(true);
    if (type === "add") {
      APICall("post", commonPayload, EndPoints.addAnnouncement)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitAnnouncement();
            handleClose();
          } else {
            toast.error(message);
          }
        
        })
        .catch((error) => {
         
          return error;
        }).finally(() => {
          Loader.isLoading(false);
        });
    } else {
      const payload = {
        id: updateData.id,
        isRemoveImage: removeImage,
        ...commonPayload,
      };
      APICall("post", payload, EndPoints.updateAnnouncement)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitAnnouncement();
            handleClose();
          } else {
            toast.error(message);
          }
         
        })
        .catch((error) => {
         
          return error;
        }).finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  const onSubmit = (values) => {
    if (!values.image || files.length === 0) {
      submitAnnouncement(values, "");
      return;
    }

   Loader.isLoading(true);
    const formData = new FormData();
    formData.append("image", values.image[0]);
    formData.append("type", "announcement");

    APICall("post", formData, EndPoints.uploadImage)
      .then((response) => {
        const { isError, data, message } = response.data;
        if (!isError) {
          submitAnnouncement(values, data.fileName);
        } else {
      
          toast.error(message);
        }
      })
      .catch((error) => {
      
        return error;
      }).finally(() => {
        Loader.isLoading(false);
      });
  };

  const handler = (mouseDownEvent) => {
    const startSize = size;
    const startPosition = { y: mouseDownEvent.pageY };

    function onMouseMove(mouseMoveEvent) {
      setSize(() => ({
        y: startSize.y - startPosition.y + mouseMoveEvent.pageY,
      }));
    }
    function onMouseUp() {
      document.body.removeEventListener("mousemove", onMouseMove);
    }

    document.body.addEventListener("mousemove", onMouseMove);
    document.body.addEventListener("mouseup", onMouseUp, { once: true });
  };

  const maxSize = width < 600 ? 230 : 150;
  const min = 0;
  const max = 100;

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 400, sm: 600 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Title"
                      placeholder="Enter title"
                      error={Boolean(errors.title)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.title && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="subTitle"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Sub Title"
                      placeholder="Enter sub title"
                      error={Boolean(errors.subTitle)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.subTitle && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  "& .MuiCardHeader-action": { lineHeight: 0.6 },
                  overflow: "visible",
                  position: "relative",
                }}
                id="card-snippet--Description"
                handler=".load"
              >
                <CardHeader
                  title={"Content"}
                  titleTypographyProps={{
                    variant: "h6",
                    fontSize: "1rem !important",
                  }}
                />
                <CardContent
                  sx={{
                    minHeight: maxSize,
                    height: size.y,
                    p: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <Controller
                      name="description"
                      control={control}
                      rules={{
                        required: false,
                      }}
                      render={({ field: { value, onChange } }) => (
                        <>  
                          <Editor
                            editorState={value}
                            editorStyle={{
                              paddingTop: 1,
                              height: size.y,
                              maxHeight: size.y - maxSize,
                            }}
                            stripPastedStyles={true}
                            wrapperStyle={{
                              textDecoration: "none",
                            }}
                            onEditorStateChange={onChange}
                            aria-describedby="validation-basic-first-name"
                            toolbar={{
                              options: EditorOption,
                            }}
                          />
                        </>
                      )}
                    />
                  </FormControl>
                </CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    p: 2,
                    cursor: "pointer",
                  }}
                  onMouseDown={handler}
                >
                  <BottomResize fontSize="small" />
                </Box>

                <Box
                  sx={{
                    height: 5,
                    display: "flex",
                    cursor: "n-resize",
                  }}
                  onMouseDown={handler}
                ></Box>
              </Card>
              {errors.description && (
                <FormHelperText sx={{ mt: 0, color: "error.main" }}>
                  This field is required
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              {type === "edit" && files.length === 0 && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      display: "flex",
                      cursor: "pointer",
                    }}
                  >
                    <Img width={100} alt="Upload img" src={updateData.image} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: false }}
                    render={() => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: ["column", "column", "row"],
                            alignItems: "center",
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: "dashed",
                            borderColor: customTheme.grey.A200,
                          }}
                        >
                          <Img
                            width={100}
                            alt="Upload img"
                            src="/images/misc/upload.png"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: ["center", "center", "inherit"],
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color="textSecondary" variant="body2">
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel
                  id="validation-basic-select"
                  error={Boolean(errors.type)}
                  htmlFor="validation-basic-select"
                >
                  Type
                </InputLabel>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value } }) => (
                    <>
                      <InputLabel
                        id="validation-basic-select"
                        error={Boolean(errors.category)}
                        htmlFor="validation-basic-select"
                      >
                        Type
                      </InputLabel>
                      <Select
                        value={value}
                        label="Type"
                        onChange={(e) => {
                          setValue("type", e.target.value);
                          clearErrors("type");
                          setSelectType(e.target.value);
                        }}
                        error={Boolean(errors.type)}
                        labelId="validation-basic-select"
                        aria-describedby="validation-basic-select"
                      >
                        <MenuItem value={0}>Non Member</MenuItem>
                        <MenuItem value={1}>Member</MenuItem>
                        <MenuItem value={2}>All</MenuItem>
                      </Select>
                    </>
                  )}
                />
                {errors.type && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="sequence"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value } }) => (
                    <TextField
                      value={value}
                      onChange={(e) => {
                        var value = parseInt(e.target.value, 10);
                        if (value > max) {
                          setError("sequence", "Enter sequence between 0-100");
                        }

                        setValue("sequence", value);
                      }}
                      fullWidth
                      className="announcement"
                      type="number"
                      inputProps={{
                        min,
                        max,
                      }}
                      label="Order Sequence"
                      placeholder="Enter order sequence between 0-100"
                      aria-describedby="validation-basic-first-name"
                      autoComplete={"off"}
                    />
                  )}
                />
                {errors.sequence && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    {errors.sequence}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {selectType === 2 && (
              <Grid item xs={12}>
                <FormControl error={Boolean(errors.priority)}>
                  <Controller
                    name="priority"
                    control={control}
                    rules={{ required: false }}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <FormControlLabel
                          sx={{
                            ...(errors.priority
                              ? { color: "error.main" }
                              : null),
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={value}
                              onChange={onChange}
                              sx={
                                errors.priority ? { color: "error.main" } : null
                              }
                            />
                          }
                          label={
                            <Typography
                              variant="body1"
                              component="span"
                              sx={{
                                color: errors.priority ? "error.main" : "",
                              }}
                            >
                              Priority
                            </Typography>
                          }
                        />
                      );
                    }}
                  />
                  {errors.priority && (
                    <FormHelperText sx={{ mt: 0, color: "error.main" }}>
                      {errors.priority.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sx={{ paddingLeft: "1rem" }}>
              <FormControl fullWidth>
                <Controller
                  name="notification"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            Send Notification
                          </Typography>
                        }
                      />
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ paddingLeft: "1rem" }}>
              <FormControl fullWidth>
                <Controller
                  name="isPin"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            Pin Announcement
                          </Typography>
                        }
                      />
                    );
                  }}
                />
              </FormControl>
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddAnnouncement;
